import axios, { AxiosError } from 'axios';
import { useAuth } from '@/shared/auth/store';

/**
 * Single axios instance for all API calls.
 * - Sends Sanctum session cookies (withCredentials).
 * - CSRF cookie fetched lazily via ensureCsrf().
 * - Attaches X-Tenant-Id if the user has picked a tenant.
 */
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

let csrfFetched = false;

export async function ensureCsrf(): Promise<void> {
  if (csrfFetched) return;
  await api.get('/sanctum/csrf-cookie');
  csrfFetched = true;
}

api.interceptors.request.use((cfg) => {
  const tenantId = localStorage.getItem('current_tenant_id');
  if (tenantId && cfg.headers) {
    cfg.headers['X-Tenant-Id'] = tenantId;
  }
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    if (err.response?.status === 401) {
      // Persisted zustand state (user, tenants, roles…) stays in localStorage
      // across a dead session. Without clearing it, the redirect below lands on
      // /login where GuestGuard still sees a `user` and bounces straight back
      // to /dashboard — /me 401s again — infinite loop. Clear first, then go.
      useAuth.getState().clear();

      const path = window.location.pathname;
      if (path !== '/login' && !path.startsWith('/forgot') && !path.startsWith('/register')) {
        // Full reload so query cache, session-scoped state and CSRF flag reset.
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  },
);

export type ApiError = AxiosError<{
  message?: string;
  errors?: Record<string, string[]>;
}>;
